import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { DiscoverSortableCard } from './discover-sortable-card'
import { useAppDispatch } from '../../hooks'
import { useGetFulfilmentCentersQuery } from '../../redux-toolkits/vendors/vendors.slice'
import { DiscoverCardPreview } from './discover-card-preview'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import {
  discoverCardApi,
  useDeleteDiscoverCardMutation,
  useGetStorefrontDiscoverCardsQuery,
  useReorderDiscoverCardsMutation,
  useGetDiscoverFeedCardsQuery,
  useHideDiscoverCardMutation,
  type AdminDiscoverCard,
} from '../../redux-toolkits/discover/discover-card-slice'
import { getInterestErrorMessage } from '../../redux-toolkits/interests/interests.helpers'
import { DiscoverCardForm, cardTypes } from './discover-card-form'

export function DiscoverCards() {
  const [storefrontId, setStorefrontId] = useState('')
  const dispatch = useAppDispatch()
  const [
    reorderCards,
    { isLoading: reordering, isSuccess: orderSaved, reset: resetOrderStatus },
  ] = useReorderDiscoverCardsMutation()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )
  const { data: centresResponse } = useGetFulfilmentCentersQuery({
    page: 1,
    limit: 1000,
  })
  const centres = centresResponse?.data.docs ?? []
  const [skip, setSkip] = useState(0)
  const pageQuery = useGetDiscoverFeedCardsQuery(
    { skip },
    { skip: storefrontId !== '' },
  )
  const storefrontQuery = useGetStorefrontDiscoverCardsQuery(storefrontId, {
    skip: storefrontId === '',
  })
  const { isFetching, isError, refetch } = storefrontId
    ? storefrontQuery
    : pageQuery
  const data = pageQuery.currentData
  const listedCards = storefrontId
    ? storefrontQuery.currentData ?? []
    : data?.cards ?? []
  const total = storefrontId ? storefrontQuery.currentData?.length : data?.total
  const sortingDisabled = !storefrontId || isFetching || isError || reordering

  const moveCard = async ({ active, over }: DragEndEvent) => {
    if (sortingDisabled || !over || active.id === over.id) return
    const from = listedCards.findIndex((card) => card._id === active.id)
    const to = listedCards.findIndex((card) => card._id === over.id)
    if (from < 0 || to < 0) return
    const reorderedCards = arrayMove(listedCards, from, to).map(
      (card, displayOrder) => ({ ...card, displayOrder }),
    )
    const update = dispatch(
      discoverCardApi.util.updateQueryData(
        'getStorefrontDiscoverCards',
        storefrontId,
        (draft) => {
          draft.splice(0, draft.length, ...reorderedCards)
        },
      ),
    )
    try {
      await reorderCards({
        storefrontId,
        cardIds: reorderedCards.map((card) => card._id),
      }).unwrap()
    } catch (error) {
      update.undo()
      toast.error(getInterestErrorMessage(error))
    }
  }
  const [editing, setEditing] = useState<AdminDiscoverCard>()
  const [showForm, setShowForm] = useState(false)
  const [removing, setRemoving] = useState<string>()
  const [deleteCard, { isLoading: deleting }] = useDeleteDiscoverCardMutation()
  const [hideCard, { isLoading: saving }] = useHideDiscoverCardMutation()
  const remove = async (id: string) => {
    try {
      await deleteCard(id).unwrap()
      setRemoving(undefined)
      toast.success('Card removed')
    } catch (error) {
      toast.error(getInterestErrorMessage(error))
    }
  }
  const hide = async (card: AdminDiscoverCard) => {
    try {
      await hideCard(card._id).unwrap()
      toast.success('Card hidden')
    } catch (error) {
      toast.error(getInterestErrorMessage(error))
    }
  }
  return (
    <section className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Discover cards</h2>
        <Button
          label="Create card"
          disabled={reordering}
          primary
          type="button"
          onClick={() => {
            setEditing(undefined)
            setShowForm(true)
          }}
        />
      </div>
      <p className="text-sm text-pry-gray">
        Create cards from existing content. Published cards join automatic
        recommendations and are matched to customers’ interests.
      </p>
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex min-w-[240px] flex-col gap-2 text-sm font-medium">
          Storefront
          <select
            className="rounded-lg border bg-white p-3"
            value={storefrontId}
            disabled={reordering}
            onChange={(event) => {
              setStorefrontId(event.target.value)
              setSkip(0)
              setShowForm(false)
              resetOrderStatus()
            }}
          >
            <option value="">All storefronts</option>
            {centres.map((centre) => (
              <option key={centre._id} value={centre._id}>
                {centre.name}
              </option>
            ))}
          </select>
        </label>
        <p
          className="pb-3 text-sm text-pry-gray"
          role="status"
          aria-live="polite"
        >
          {!storefrontId && 'Choose a storefront to drag cards into position.'}
          {storefrontId && reordering && 'Saving card order…'}
          {storefrontId &&
            !reordering &&
            orderSaved &&
            'Card order saved. Drag a card to move it again.'}
          {storefrontId &&
            !reordering &&
            !orderSaved &&
            'Drag cards into position. Changes save automatically.'}
        </p>
      </div>
      {showForm && (
        <DiscoverCardForm
          key={editing?._id ?? 'new'}
          card={editing}
          onClose={() => setShowForm(false)}
        />
      )}
      {isFetching && <p>Loading cards…</p>}
      {isError && (
        <p role="alert">
          Unable to load cards.{' '}
          <button
            type="button"
            onClick={() => {
              void refetch()
            }}
          >
            Retry
          </button>
        </p>
      )}
      {total === 0 && (
        <p>
          No admin cards yet. Automatic recommendations continue to populate the
          feed.
        </p>
      )}
      <p className="text-xs text-pry-gray">
        Card previews use current content. Your curated cards keep their saved
        order among personalised recommendations. Availability, likes and follow
        state vary by customer.
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={moveCard}
      >
        <SortableContext
          items={listedCards.map((card) => card._id)}
          strategy={rectSortingStrategy}
        >
          <div className="grid items-start gap-6 md:grid-cols-2 xl:grid-cols-3">
            {listedCards.map((card, index) => (
              <DiscoverSortableCard
                key={card._id}
                id={card._id}
                title={card.title}
                position={storefrontId ? index + 1 : undefined}
                disabled={sortingDisabled}
              >
                <DiscoverCardPreview
                  key={card.updatedAt}
                  preview={card.preview}
                />
                <h3 className="font-medium">{card.title}</h3>
                <p>
                  {cardTypes.find((type) => type.value === card.type)?.label} ·{' '}
                  {card.status}
                </p>
                {card.startsAt != null && (
                  <p className="text-sm">
                    Starts {new Date(card.startsAt).toLocaleString()}
                  </p>
                )}
                {card.endsAt != null && (
                  <p className="text-sm">
                    Ends {new Date(card.endsAt).toLocaleString()}
                  </p>
                )}
                <div className="flex gap-4">
                  <button
                    type="button"
                    disabled={reordering}
                    onClick={() => {
                      setEditing(card)
                      setShowForm(true)
                    }}
                  >
                    Edit
                  </button>
                  {card.status === 'published' && (
                    <button
                      type="button"
                      disabled={saving || reordering}
                      onClick={() => {
                        void hide(card)
                      }}
                    >
                      Hide
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-danger"
                    disabled={reordering}
                    onClick={() => setRemoving(card._id)}
                  >
                    Delete
                  </button>
                </div>
                {removing === card._id && (
                  <div>
                    <p>Delete this feed card?</p>
                    <button
                      type="button"
                      disabled={deleting || reordering}
                      onClick={() => {
                        void remove(card._id)
                      }}
                    >
                      Confirm
                    </button>
                    <button
                      className="ml-4"
                      type="button"
                      onClick={() => setRemoving(undefined)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </DiscoverSortableCard>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {storefrontId && total !== undefined && (
        <p className="text-sm text-pry-gray">{total} cards</p>
      )}
      {!storefrontId && data !== undefined && (
        <div className="flex gap-4 items-center">
          <Button
            type="button"
            label="Previous"
            disabled={skip === 0 || isFetching}
            onClick={() => setSkip(Math.max(0, skip - data.limit))}
          />
          <span>{data.total} cards</span>
          <Button
            type="button"
            label="Next"
            disabled={skip + data.limit >= data.total || isFetching}
            onClick={() => setSkip(skip + data.limit)}
          />
        </div>
      )}
    </section>
  )
}
